// Tier Configuration and Feature Management
export interface TierFeature {
  id: string;
  name: string;
  description: string;
  category: 'core' | 'analytics' | 'automation' | 'integration' | 'ai' | 'collaboration' | 'enterprise';
  icon: string;
  route?: string;
  comingSoon?: boolean;
}

export interface TierConfig {
  id: 'free' | 'smb' | 'professional' | 'enterprise';
  name: string;
  displayName: string;
  description: string;
  price: {
    monthly: number;
    yearly: number;
  };
  color: string;
  bgColor: string;
  borderColor: string;
  features: TierFeature[];
  limits: {
    campaigns: number;
    teamMembers: number;
    apiCalls: number;
    storage: string;
    support: string;
    addOns: number; // Maximum number of add-ons allowed (-1 for unlimited)
  };
  routes: string[];
}

// Core Features (Available to all tiers)
export const CORE_FEATURES: TierFeature[] = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Basic overview & metrics',
    category: 'core',
    icon: 'LayoutDashboard',
    route: '/dashboard'
  },
  {
    id: 'campaigns-basic',
    name: 'Basic Campaign Management',
    description: 'Create and manage basic marketing campaigns',
    category: 'core',
    icon: 'Target',
    route: '/campaigns'
  },
  {
    id: 'studio-basic',
    name: 'Basic Content Studio',
    description: 'Basic AI-powered content creation (5 templates)',
    category: 'core',
    icon: 'FileText',
    route: '/studio'
  },
  {
    id: 'analytics-basic',
    name: 'Basic Analytics',
    description: 'Performance analytics & insights',
    category: 'core',
    icon: 'BarChart3',
    route: '/analytics'
  },
  {
    id: 'ai-assistant-basic',
    name: 'AI Assistant',
    description: 'AI-powered chat assistant',
    category: 'ai',
    icon: 'Bot',
    route: '/ai-assistant'
  }
];

// Professional Tier Additional Features (renamed from Medium)
export const PROFESSIONAL_FEATURES: TierFeature[] = [
  {
    id: 'campaigns-advanced',
    name: 'Advanced Campaign Management',
    description: 'Advanced targeting & optimization',
    category: 'core',
    icon: 'Target',
    route: '/campaigns'
  },
  {
    id: 'team-collaboration',
    name: 'Team Collaboration',
    description: 'Team management & sharing',
    category: 'collaboration',
    icon: 'Users',
    route: '/team'
  },
  {
    id: 'analytics-enhanced',
    name: 'Enhanced Analytics',
    description: 'Enhanced analytics dashboard',
    category: 'analytics',
    icon: 'TrendingUp',
    route: '/analytics'
  },
  {
    id: 'automations-basic',
    name: 'Basic Workflow Automation',
    description: 'Basic workflow automation',
    category: 'automation',
    icon: 'Zap',
    route: '/automations'
  },
  {
    id: 'integrations-hub',
    name: 'Integrations Hub',
    description: 'Connect popular tools (10 integrations)',
    category: 'integration',
    icon: 'Link',
    route: '/integrations'
  },
  {
    id: 'ai-assistant-pro',
    name: 'AI Assistant Pro',
    description: 'Enhanced AI (500 requests/month)',
    category: 'ai',
    icon: 'Brain',
    route: '/ai-center'
  }
];

// Enterprise Tier Additional Features
export const ENTERPRISE_FEATURES: TierFeature[] = [
  {
    id: 'white-label',
    name: 'White Label Solution',
    description: 'Custom branding & white-labeling',
    category: 'enterprise',
    icon: 'Palette',
    route: '/white-label'
  },
  {
    id: 'api-access',
    name: 'Full API Access',
    description: 'Complete API integration capabilities',
    category: 'enterprise',
    icon: 'Code',
    route: '/api'
  },
  {
    id: 'sso-security',
    name: 'SSO & Enterprise Security',
    description: 'Enterprise authentication & security',
    category: 'enterprise',
    icon: 'Shield',
    route: '/security'
  },
  {
    id: 'dedicated-support',
    name: 'Dedicated Account Manager',
    description: '24/7 phone + dedicated account manager',
    category: 'enterprise',
    icon: 'Headphones',
    route: '/support'
  },
  {
    id: 'custom-reporting',
    name: 'Custom Dashboard Builder',
    description: 'Build unlimited custom dashboards',
    category: 'enterprise',
    icon: 'PieChart',
    route: '/custom-dashboards'
  },
  {
    id: 'unlimited-ai',
    name: 'Unlimited AI Requests',
    description: 'No limits on AI requests and processing',
    category: 'ai',
    icon: 'Brain',
    route: '/ai-center'
  }
];

// Add-On Features Configuration
export interface AddOnFeature {
  id: string;
  name: string;
  description: string;
  category: 'content' | 'sales' | 'intelligence' | 'ai' | 'analytics' | 'enterprise' | 'research' | 'social' | 'optimization';
  price: {
    monthly: number;
    yearly: number;
  };
  icon: string;
  pages: string[];
  targetTiers: ('smb' | 'professional' | 'enterprise')[];
  roi: string;
}

// Add-On Features
export const ADDON_FEATURES: AddOnFeature[] = [
  {
    id: 'content-calendar-pro',
    name: 'Content Calendar Pro',
    description: 'Advanced content planning & scheduling',
    category: 'content',
    price: { monthly: 39, yearly: 390 },
    icon: 'Calendar',
    pages: ['/content-calendar'],
    targetTiers: ['smb', 'professional'],
    roi: 'Save 10+ hours/week on content planning'
  },
  {
    id: 'lead-scoring-management',
    name: 'Lead Scoring & Management',
    description: 'AI-powered lead scoring & qualification',
    category: 'sales',
    price: { monthly: 79, yearly: 790 },
    icon: 'Target',
    pages: ['/lead-scoring'],
    targetTiers: ['smb', 'professional'],
    roi: 'Increase conversion rates by 25-40%'
  },
  {
    id: 'advanced-rag-knowledge',
    name: 'Advanced RAG & Knowledge',
    description: 'Custom knowledge base & intelligent processing',
    category: 'ai',
    price: { monthly: 149, yearly: 1490 },
    icon: 'Brain',
    pages: ['/rag-knowledge'],
    targetTiers: ['professional', 'enterprise'],
    roi: 'Leverage company knowledge for better AI outputs'
  },
  {
    id: 'marketing-research',
    name: 'Marketing Research Suite',
    description: 'AI-powered market analysis & competitor insights',
    category: 'research',
    price: { monthly: 89, yearly: 890 },
    icon: 'BarChart3',
    pages: ['/marketing-research'],
    targetTiers: ['smb', 'professional', 'enterprise'],
    roi: 'Make data-driven decisions with 90% accuracy'
  },
  {
    id: 'customer-journey-mapping',
    name: 'Customer Journey Mapping',
    description: 'Visualize and optimize customer touchpoints',
    category: 'analytics',
    price: { monthly: 69, yearly: 690 },
    icon: 'Users',
    pages: ['/customer-journey'],
    targetTiers: ['professional', 'enterprise'],
    roi: 'Improve customer experience by 35%'
  },
  {
    id: 'social-media-enhanced',
    name: 'Social Media Enhanced',
    description: 'Advanced social media management & automation',
    category: 'social',
    price: { monthly: 59, yearly: 590 },
    icon: 'Share2',
    pages: ['/social-media-enhanced'],
    targetTiers: ['smb', 'professional'],
    roi: 'Increase social engagement by 50%'
  },
  {
    id: 'seo-content-optimizer',
    name: 'SEO Content Optimizer',
    description: 'AI-powered SEO optimization & content analysis',
    category: 'content',
    price: { monthly: 49, yearly: 490 },
    icon: 'Search',
    pages: ['/seo-content'],
    targetTiers: ['smb', 'professional', 'enterprise'],
    roi: 'Boost organic traffic by 60%'
  },
  {
    id: 'conversion-optimization',
    name: 'Conversion Optimization',
    description: 'A/B testing & conversion rate optimization tools',
    category: 'optimization',
    price: { monthly: 79, yearly: 790 },
    icon: 'TrendingUp',
    pages: ['/conversion-optimization'],
    targetTiers: ['professional', 'enterprise'],
    roi: 'Increase conversion rates by 40%'
  },
  {
    id: 'customer-feedback-analyzer',
    name: 'Customer Feedback Analyzer',
    description: 'AI sentiment analysis & feedback insights',
    category: 'analytics',
    price: { monthly: 59, yearly: 590 },
    icon: 'MessageSquare',
    pages: ['/customer-feedback'],
    targetTiers: ['smb', 'professional', 'enterprise'],
    roi: 'Improve customer satisfaction by 45%'
  },
  {
    id: 'customer-retention-suite',
    name: 'Customer Retention Suite',
    description: 'AI-powered churn prediction & loyalty campaigns',
    category: 'analytics',
    price: { monthly: 79, yearly: 790 },
    icon: 'Heart',
    pages: ['/customer-retention'],
    targetTiers: ['professional', 'enterprise'],
    roi: 'Reduce churn by 30% and increase customer LTV'
  },
  {
    id: 'customer-insights-pro',
    name: 'Customer Insights Pro',
    description: 'Advanced customer analytics & behavioral insights',
    category: 'analytics',
    price: { monthly: 99, yearly: 990 },
    icon: 'Users',
    pages: ['/customer-insights'],
    targetTiers: ['smb', 'professional'],
    roi: 'Increase customer understanding by 60% and boost targeting accuracy'
  }
];

// Tier Configurations
export const TIER_CONFIGS: Record<string, TierConfig> = {
  smb: {
    id: 'smb',
    name: 'SMB',
    displayName: 'Small Business',
    description: 'AI-Powered Marketing Essentials',
    price: {
      monthly: 79,
      yearly: 790
    },
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    features: [...CORE_FEATURES],
    limits: {
      campaigns: 10,
      teamMembers: 3,
      apiCalls: 100, // AI requests per month
      storage: '5GB',
      support: 'Email only',
      addOns: 3 // Maximum 3 add-ons for SMB tier
    },
    routes: [
      '/smb',
      '/smb/dashboard',
      '/smb/campaigns',
      '/smb/studio',
      '/smb/reports',
      '/smb/ai-center'
    ]
  },
  professional: {
    id: 'professional',
    name: 'Professional',
    displayName: 'Professional',
    description: 'Complete Marketing Automation Suite',
    price: {
      monthly: 199,
      yearly: 1990
    },
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    features: [...CORE_FEATURES, ...PROFESSIONAL_FEATURES],
    limits: {
      campaigns: 50,
      teamMembers: 10,
      apiCalls: 500, // AI requests per month
      storage: '50GB',
      support: 'Priority email + chat',
      addOns: 7 // Maximum 7 add-ons for Professional tier
    },
    routes: [
      '/professional',
      '/professional/dashboard',
      '/professional/campaigns',
      '/professional/studio',
      '/professional/reports',
      '/professional/analytics',
      '/professional/automations',
      '/professional/integrations',
      '/professional/ai-center',
      '/professional/team'
    ]
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    displayName: 'Enterprise',
    description: 'Custom AI Marketing Platform',
    price: {
      monthly: 799, // Starting price, actual pricing is "Contact Us"
      yearly: 7990
    },
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    features: [...CORE_FEATURES, ...PROFESSIONAL_FEATURES, ...ENTERPRISE_FEATURES],
    limits: {
      campaigns: -1, // Unlimited
      teamMembers: -1, // Unlimited
      apiCalls: -1, // Unlimited AI requests
      storage: 'Unlimited',
      support: '24/7 phone + dedicated manager',
      addOns: -1 // Unlimited add-ons for Enterprise tier
    },
    routes: [
      '/enterprise',
      '/enterprise/dashboard',
      '/enterprise/campaigns',
      '/enterprise/studio',
      '/enterprise/reports',
      '/enterprise/analytics',
      '/enterprise/automations',
      '/enterprise/integrations',
      '/enterprise/ai-center',
      '/enterprise/team',
      '/enterprise/white-label',
      '/enterprise/api',
      '/enterprise/custom-dashboards',
      '/enterprise/security',
      '/enterprise/support'
    ]
  }
};

// Helper Functions
export const getTierConfig = (tier: 'free' | 'smb' | 'professional' | 'enterprise'): TierConfig => {
  // Free tier doesn't have a full config in TIER_CONFIGS, return a minimal config
  if (tier === 'free') {
    return {
      id: 'free',
      name: 'free',
      displayName: 'Free',
      description: 'Try Plugly AI with limited features',
      price: { monthly: 0, yearly: 0 },
      color: 'text-blue-400',
      bgColor: 'from-blue-500/10 to-cyan-500/10',
      borderColor: 'border-blue-500',
      features: [],
      limits: {
        campaigns: 1, // 1 campaign (view-only after creation)
        teamMembers: 1,
        apiCalls: 10, // 10 AI requests/month (hard limit)
        storage: '500MB', // Reduced from 1GB
        support: 'Community',
        addOns: 0
      },
      routes: ['/free/dashboard', '/free/campaigns', '/free/analytics', '/free/billing', '/free/help']
    };
  }
  return TIER_CONFIGS[tier];
};

export const getTierFeatures = (tier: 'free' | 'smb' | 'professional' | 'enterprise'): TierFeature[] => {
  if (tier === 'free') {
    return [];
  }
  return TIER_CONFIGS[tier].features;
};

export const hasFeatureAccess = (tier: 'free' | 'smb' | 'professional' | 'enterprise', featureId: string): boolean => {
  const config = getTierConfig(tier);
  return config.features.some(feature => feature.id === featureId);
};

export const getAvailableRoutes = (tier: 'free' | 'smb' | 'professional' | 'enterprise'): string[] => {
  // Free tier has limited routes
  if (tier === 'free') {
    return ['/free/dashboard', '/free/ai-assistant', '/free/upload', '/free/billing', '/free/help'];
  }
  return TIER_CONFIGS[tier].routes;
};

export const isRouteAllowed = (tier: 'free' | 'smb' | 'professional' | 'enterprise', route: string): boolean => {
  const allowedRoutes = getAvailableRoutes(tier);
  return allowedRoutes.some(allowedRoute => route.startsWith(allowedRoute));
};

// Add-On Helper Functions
export const getAddOnsByTier = (tier: 'free' | 'smb' | 'professional' | 'enterprise'): AddOnFeature[] => {
  if (tier === 'free') {
    return [];
  }
  return ADDON_FEATURES.filter(addon => addon.targetTiers.includes(tier));
};

export const getAddOnById = (id: string): AddOnFeature | undefined => {
  return ADDON_FEATURES.find(addon => addon.id === id);
};

export const hasAddOnAccess = (userAddOns: string[], addOnId: string): boolean => {
  return userAddOns.includes(addOnId);
};

export const calculateAddOnPrice = (addOnIds: string[], billingCycle: 'monthly' | 'yearly'): number => {
  return addOnIds.reduce((total, id) => {
    const addon = getAddOnById(id);
    return total + (addon ? addon.price[billingCycle] : 0);
  }, 0);
};

// Add-On Limit Helper Functions
export const getAddOnLimit = (tier: 'smb' | 'professional' | 'enterprise'): number => {
  return TIER_CONFIGS[tier].limits.addOns;
};

export const canPurchaseMoreAddOns = (tier: 'smb' | 'professional' | 'enterprise', currentAddOnCount: number): boolean => {
  const limit = getAddOnLimit(tier);
  if (limit === -1) return true; // Unlimited
  return currentAddOnCount < limit;
};

export const getRemainingAddOnSlots = (tier: 'smb' | 'professional' | 'enterprise', currentAddOnCount: number): number => {
  const limit = getAddOnLimit(tier);
  if (limit === -1) return Infinity; // Unlimited
  return Math.max(0, limit - currentAddOnCount);
};
